import { useEffect, useMemo, useRef } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import type { ProductFeeFormInput } from '../schemas/productFee.schema';
import type { FeeReferenceData } from '../services/productFees';
import type { FeeRecord } from '../services/smr-admin';
import { payerPairForFee } from '../utils/payerPair';

interface Params {
    feeId: string;
    fees: Pick<FeeRecord, 'id' | 'transaction_type_id'>[];
    transactionTypes: FeeReferenceData['transaction_types'] | undefined;
    overridden: boolean;
    setValue: UseFormSetValue<ProductFeeFormInput>;
}

interface Result {
    locked: boolean;
    unroutedCode: string | null;
}

export function useFeePayerPairDerivation({ feeId, fees, transactionTypes, overridden, setValue }: Params): Result {
    const pair = useMemo(
        () => payerPairForFee(feeId, fees, transactionTypes ?? []),
        [feeId, fees, transactionTypes],
    );
    const paidById = pair.status === 'derived' ? pair.paidById : null;
    const paidToId = pair.status === 'derived' ? pair.paidToId : null;

    const wasDerived = useRef(false);
    const isUnknown = pair.status === 'unknown';

    useEffect(() => {
        if (overridden) {
            return;
        }
        if (paidById !== null && paidToId !== null) {
            wasDerived.current = true;
            setValue('paid_by_id', paidById, { shouldValidate: true });
            setValue('paid_to_id', paidToId, { shouldValidate: true });
            return;
        }
        if (isUnknown && wasDerived.current) {
            setValue('paid_by_id', '', { shouldValidate: true });
            setValue('paid_to_id', '', { shouldValidate: true });
        }
        wasDerived.current = false;
    }, [overridden, paidById, paidToId, isUnknown, setValue]);

    if (overridden) {
        return { locked: true, unroutedCode: null };
    }

    return {
        locked: pair.status === 'derived',
        unroutedCode: pair.status === 'unrouted' ? pair.code : null,
    };
}
